import { useAuthStore } from '@/stores/authStore';
import { supabase } from '../services/supabase';
import type { Session } from '@supabase/supabase-js';

export interface SessionValidationResult {
  isValid: boolean;
  session: Session | null;
  error?: string;
  refreshed?: boolean;
}

/**
 * Safely retrieves the current user ID from the auth store
 * Returns null if user is not authenticated or ID is invalid
 */
export function getSafeUserId(): string | null {
  const { user } = useAuthStore.getState();
  return user?.id && user.id !== 'undefined' && typeof user.id === 'string' ? user.id : null;
}

/**
 * Ensures the current session is valid and proactively refreshes if needed
 * Refreshes sessions that expire within 2 minutes
 */
export async function ensureValidSession(): Promise<SessionValidationResult> {
  try {
    // Get current session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      console.error('Session retrieval error:', sessionError);
      return {
        isValid: false,
        session: null,
        error: `Session retrieval failed: ${sessionError.message}`
      };
    }

    if (!session) {
      return {
        isValid: false,
        session: null,
        error: 'No active session found'
      };
    }

    // Check if session expires within 2 minutes (120 seconds)
    const currentTime = Math.floor(Date.now() / 1000);
    const expiresAt = session.expires_at;
    const timeToExpiry = expiresAt ? expiresAt - currentTime : 0;
    
    // If session expires within 2 minutes, proactively refresh
    if (timeToExpiry <= 120) {
      console.log(`Session expires in ${timeToExpiry}s, refreshing proactively`);
      
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
      
      if (refreshError) {
        console.error('Session refresh error:', refreshError);
        return {
          isValid: false,
          session: null,
          error: `Session refresh failed: ${refreshError.message}`
        };
      }

      if (!refreshData.session) {
        return {
          isValid: false,
          session: null,
          error: 'Session refresh returned no session'
        };
      }

      console.log('Session refreshed successfully');
      return {
        isValid: true,
        session: refreshData.session,
        refreshed: true
      };
    }

    // Session is valid and doesn't need refresh
    return {
      isValid: true,
      session,
      refreshed: false
    };

  } catch (error) {
    console.error('Unexpected error in ensureValidSession:', error);
    return {
      isValid: false,
      session: null,
      error: error instanceof Error ? error.message : 'Unknown session validation error'
    };
  }
}

/**
 * Retry an operation with automatic session refresh on auth failures
 */
export async function retryWithSessionRefresh<T>(
  operation: (session: Session) => Promise<T>,
  maxRetries: number = 1
): Promise<T> {
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const sessionResult = await ensureValidSession();
      
      if (!sessionResult.isValid || !sessionResult.session) {
        throw new Error(sessionResult.error || 'Invalid session');
      }

      // Attempt the operation with valid session
      return await operation(sessionResult.session);

    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');
      
      // Check if it's an auth-related error that might benefit from retry
      const isAuthError = lastError.message.includes('401') || 
                         lastError.message.includes('unauthorized') ||
                         lastError.message.includes('token') ||
                         lastError.message.includes('session');
      
      if (attempt < maxRetries && isAuthError) {
        console.log(`Auth error on attempt ${attempt + 1}, retrying...`);
        // Force a session refresh by clearing current session first
        await supabase.auth.refreshSession();
        continue;
      }
      
      // If not an auth error or max retries reached, throw the error
      break;
    }
  }
  
  throw lastError || new Error('Operation failed after retries');
}

/**
 * Check if current user is authenticated with valid session
 */
export async function isAuthenticated(): Promise<boolean> {
  const result = await ensureValidSession();
  return result.isValid;
}

/**
 * Get current user with session validation
 */
export async function getCurrentUser() {
  const result = await ensureValidSession();
  
  if (!result.isValid || !result.session) {
    return null;
  }
  
  return result.session.user;
}

/**
 * Sign out and clear session
 */
export async function signOut(): Promise<{ error?: string }> {
  try {
    const { error } = await supabase.auth.signOut();
    
    if (error) {
      return { error: error.message };
    }
    
    return {};
  } catch (error) {
    return { 
      error: error instanceof Error ? error.message : 'Sign out failed' 
    };
  }
}